import { Modal, Table, Badge, Spinner } from 'flowbite-react';
import { useQuery } from '@tanstack/react-query';
import CustomModalHeader from 'components/modal/customModalHeader';
import { getStockouts } from 'api/stockApi';
import { themModal } from 'utils/theme';
import { stockoutType } from 'utils/proptypes';

type StockoutHistoryModalProps = {
  openModal: boolean;
  setOpenModal: (isOpen: boolean) => void;
  stockId: string;
  itemName?: string;
  locationName?: string;
};

const formatDate = (dateStr: string | null): string => {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

const StockoutHistoryModal = (props: StockoutHistoryModalProps) => {
  const { openModal, setOpenModal, stockId, itemName, locationName } = props;

  const { data, isLoading } = useQuery(
    ['stockout-history', stockId],
    () => getStockouts({ stockId, limit: 50 }),
    { enabled: openModal && !!stockId, staleTime: 0 },
  );

  const stockouts: stockoutType[] = data?.data?.entities ?? [];

  const totalEstimatedLost = stockouts
    .filter((s) => s.estimatedLostSales !== null)
    .reduce((sum, s) => sum + (s.estimatedLostSales ?? 0), 0);

  return (
    <Modal
      show={openModal}
      size="4xl"
      onClose={() => setOpenModal(false)}
      theme={themModal}
    >
      <Modal.Body className="p-0">
        <CustomModalHeader
          title={`Stockout History${itemName ? ` — ${itemName}` : ''}${locationName ? ` @ ${locationName}` : ''}`}
          toggle={() => setOpenModal(false)}
        />

        <div className="px-6 pb-6 pt-4">
          {isLoading ? (
            <div className="flex justify-center items-center py-12">
              <Spinner size="lg" />
            </div>
          ) : stockouts.length === 0 ? (
            <div className="text-center text-gray-500 py-12">
              <p className="text-sm">No stockout events recorded for this item.</p>
            </div>
          ) : (
            <>
              {/* Summary row */}
              <div className="flex gap-6 mb-4 p-3 bg-gray-50 rounded-lg text-sm">
                <div>
                  <span className="text-gray-500">Total stockouts:</span>{' '}
                  <span className="font-semibold">{stockouts.length}</span>
                </div>
                <div>
                  <span className="text-gray-500">Currently in stockout:</span>{' '}
                  {stockouts.some((s) => s.isOngoing) ? (
                    <Badge color="failure" className="inline-flex">Yes</Badge>
                  ) : (
                    <span className="font-semibold text-green-600">No</span>
                  )}
                </div>
                {totalEstimatedLost > 0 && (
                  <div>
                    <span className="text-gray-500">Total est. lost sales:</span>{' '}
                    <span className="font-semibold text-red-600">{totalEstimatedLost.toFixed(0)} units</span>
                  </div>
                )}
              </div>

              <div className="overflow-x-auto">
                <Table hoverable>
                  <Table.Head>
                    <Table.HeadCell>Status</Table.HeadCell>
                    <Table.HeadCell>Start Date</Table.HeadCell>
                    <Table.HeadCell>Restock Date</Table.HeadCell>
                    <Table.HeadCell>Duration (days)</Table.HeadCell>
                    <Table.HeadCell>Est. Lost Sales (units)</Table.HeadCell>
                  </Table.Head>
                  <Table.Body>
                    {stockouts.map((s: stockoutType) => (
                      <Table.Row
                        key={s.id}
                        className={s.isOngoing ? 'bg-red-50' : 'bg-white'}
                      >
                        <Table.Cell>
                          {s.isOngoing ? (
                            <Badge color="failure">Ongoing</Badge>
                          ) : (
                            <Badge color="success">Resolved</Badge>
                          )}
                        </Table.Cell>
                        <Table.Cell className="font-medium text-gray-900">
                          {formatDate(s.startDate)}
                        </Table.Cell>
                        <Table.Cell className="text-gray-600">
                          {s.isOngoing ? (
                            <span className="italic text-gray-400">In progress</span>
                          ) : (
                            formatDate(s.restockDate)
                          )}
                        </Table.Cell>
                        <Table.Cell className="text-gray-600">
                          {s.durationDays !== null ? s.durationDays : (
                            <span className="italic text-gray-400">—</span>
                          )}
                        </Table.Cell>
                        <Table.Cell className={s.estimatedLostSales ? 'text-red-600 font-medium' : 'text-gray-400 italic'}>
                          {s.estimatedLostSales !== null
                            ? `~${Number(s.estimatedLostSales).toFixed(0)}`
                            : '—'}
                        </Table.Cell>
                      </Table.Row>
                    ))}
                  </Table.Body>
                </Table>
              </div>

              <p className="mt-3 text-xs text-gray-400">
                Est. lost sales = (avg weekly sales ÷ 7) × duration days, based on the 6-week sales mean.
              </p>
            </>
          )}
        </div>
      </Modal.Body>
    </Modal>
  );
};

export default StockoutHistoryModal;
